import {
  VideoObjectPaginatedList,
  VideosVideoIdDeleteRequest,
  VideosVideoIdPatchRequest,
  VideosPostRequest,
  VideosVideoIdBlobsPostRequest,
  VideosVideoIdBlobsBlobIdPatchRequest,
  VideoBlobPaginatedList,
  VideosVideoIdBlobsBlobIdDeleteRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'

import { ContentAPI } from 'services/api'
import { videoObjectQueryKeys } from 'services/queryKeys'
import { updateListCachesByCreation, updateListCachesByDeletion, updateListCachesByUpdate } from 'utils'

export const videoObjectMutationKeys = {
  delete: () => ['content', 'video-object', 'DELETE'] as const,
}

type UseVideoObjectCreateMutationProps = {
  originId?: string
}

export const useVideoObjectCreateMutation = (props: UseVideoObjectCreateMutationProps) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: VideosPostRequest) => ContentAPI.videoObjects.videosPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: videoObjectQueryKeys.lists(),
      })
    },
    onSettled: (data, error) => {
      if (!!data && !error) {
        queryClient.setQueriesData<InfiniteData<VideoObjectPaginatedList>>(
          { queryKey: videoObjectQueryKeys.lists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props.originId }),
        )
      }
    },
  })
}

export const useVideoObjectUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: VideosVideoIdPatchRequest) => ContentAPI.videoObjects.videosVideoIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.batchGets() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: videoObjectQueryKeys.batchGets() })

        queryClient.setQueriesData<InfiniteData<VideoObjectPaginatedList>>(
          { queryKey: videoObjectQueryKeys.lists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.videoId }),
        )

        return queryClient.invalidateQueries({ queryKey: videoObjectQueryKeys.details() })
      }
    },
  })
}

export const useVideoObjectDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: videoObjectMutationKeys.delete(),
    mutationFn: (params: VideosVideoIdDeleteRequest) => ContentAPI.videoObjects.videosVideoIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.lists() })
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.batchGets() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<VideoObjectPaginatedList>>(
          { queryKey: videoObjectQueryKeys.lists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.videoId }),
        )
      }
    },
  })
}

export const useVideoObjectBlobCreateMutation = (props?: { originId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: VideosVideoIdBlobsPostRequest) => ContentAPI.videoObjects.videosVideoIdBlobsPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.blobLists() })
    },
    onSettled: (data, error) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: videoObjectQueryKeys.blobLists() })

        queryClient.setQueriesData<InfiniteData<VideoBlobPaginatedList>>(
          { queryKey: videoObjectQueryKeys.blobLists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props?.originId }),
        )
      }
    },
  })
}

export const useVideoObjectBlobUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: VideosVideoIdBlobsBlobIdPatchRequest) =>
      ContentAPI.videoObjects.videosVideoIdBlobsBlobIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.blobLists() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: videoObjectQueryKeys.blobLists() })

        queryClient.setQueriesData<InfiniteData<VideoBlobPaginatedList>>(
          { queryKey: videoObjectQueryKeys.blobLists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.blobId }),
        )

        return queryClient.invalidateQueries({ queryKey: videoObjectQueryKeys.blobDetails() })
      }
    },
  })
}

export const useVideoObjectBlobDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: VideosVideoIdBlobsBlobIdDeleteRequest) =>
      ContentAPI.videoObjects.videosVideoIdBlobsBlobIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.blobLists() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<VideoBlobPaginatedList>>(
          { queryKey: videoObjectQueryKeys.blobLists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.blobId }),
        )
      }
    },
  })
}
